import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, NotebookText, Plus } from 'lucide-react';
import { Button } from 'components/ui/button';
import { PageHeader } from 'components/ui/page-header';
import EmptyState from 'components/ui/EmptyState';
import Skeleton from 'components/ui/skeleton';
import * as Notebooks from 'modules/notebooks';
import { usePermission } from 'modules/auth/hooks';
import { PERMISSIONS } from 'modules/auth/permissions';
import { useIsMobile } from 'hooks/use-media-query';
import { NotebooksMobile } from 'components/mobile/screens/NotebooksMobile';
import { CreateTile } from 'pages/Library/CreateTile';
import { NotebookCard } from './NotebookCard';
import { NotebookDialogs, type NotebookDialog } from './NotebookDialogs';

const GRID = 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

/**
 * `/app/documents`: the user's notebooks. Opening one opens the AI Generate workspace scoped to it.
 * The route is already gated by DOCUMENTS_EDIT, so creating and renaming need no further check;
 * deleting also needs DOCUMENTS_DELETE (the API enforces both).
 */
const NotebooksDesktop: React.FC = () => {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<NotebookDialog | null>(null);
  const canDelete = usePermission(PERMISSIONS.DOCUMENTS_DELETE);
  const { notebooks, isLoading, isError, refetch } = Notebooks.Hooks.useList();

  const openCreate = () => setDialog({ kind: 'create' });
  const displayTitle = (notebook: Notebooks.Types.Notebook) =>
    Notebooks.getNotebookTitle(notebook, t('notebooks.default_title'));

  const renderBody = () => {
    if (isLoading) {
      return (
        <div aria-busy="true">
          <span role="status" className="sr-only">
            {t('loading')}
          </span>
          <div className={GRID} aria-hidden>
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className="h-[148px] rounded-lg" />
            ))}
          </div>
        </div>
      );
    }
    if (isError) {
      return (
        <EmptyState
          icon={<AlertCircle size={28} />}
          title={t('failed_to_load')}
          secondaryAction={{ label: t('retry'), onClick: () => refetch() }}
        />
      );
    }
    if (notebooks.length === 0) {
      return (
        <EmptyState
          icon={<NotebookText size={28} />}
          title={t('notebooks.empty_title')}
          description={t('notebooks.empty_desc')}
          action={{ label: t('notebooks.new_notebook'), icon: <Plus className="h-4 w-4" />, onClick: openCreate }}
        />
      );
    }
    return (
      <ul className={GRID}>
        <li className="list-none">
          <CreateTile label={t('notebooks.new_notebook')} onClick={openCreate} className="h-full w-full" />
        </li>
        {notebooks.map((notebook) => (
          <NotebookCard
            key={notebook._id}
            notebook={notebook}
            title={displayTitle(notebook)}
            onRename={(n) => setDialog({ kind: 'rename', notebook: n, title: displayTitle(n) })}
            onDelete={canDelete ? (n) => setDialog({ kind: 'delete', notebook: n, title: displayTitle(n) }) : undefined}
          />
        ))}
      </ul>
    );
  };

  return (
    <div id="documents-page-container" data-tour="documents-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('notebooks.title')}
        description={t('notebooks.subtitle')}
        actions={
          <Button size="sm" className="gap-1.5" onClick={openCreate}>
            <Plus className="h-4 w-4" />
            {t('notebooks.new_notebook')}
          </Button>
        }
      />

      {renderBody()}

      <NotebookDialogs dialog={dialog} onClose={() => setDialog(null)} />
    </div>
  );
};

// Phones render the dedicated mobile list; thin wrapper keeps hook count
// constant across the breakpoint.
const NotebooksPage: React.FC = () => (useIsMobile() ? <NotebooksMobile /> : <NotebooksDesktop />);

export default NotebooksPage;
